// client/src/pages/Login.jsx
import React, { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";

export default function Login() {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  // ✅ Always works even if env is missing
  const API_BASE = useMemo(() => {
    const raw = (process.env.REACT_APP_API_URL || "").trim();
    const cleaned = raw.replace(/\/$/, "").replace(/\/api$/, ""); // prevent .../api/api
    return cleaned || "https://streamsite-v8dd.onrender.com";
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError("");

    try {
      const res = await fetch(`${API_BASE}/api/auth/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, password }),
      });

      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.message || data?.error || "Sign in failed");

      // store both formats (your app uses both)
      localStorage.setItem("token", data.token);
      localStorage.setItem("ga_token", data.token);
      localStorage.setItem("authToken", data.token);

      if (data.user) {
        localStorage.setItem("user", JSON.stringify(data.user));
        localStorage.setItem("ga_user", JSON.stringify(data.user));
        if (data.user.email) localStorage.setItem("member_email", String(data.user.email).trim().toLowerCase());
      }

      window.dispatchEvent(new Event("auth:changed"));

      // ✅ If login was triggered from an OBS dock / deep-link, return there
      const params = new URLSearchParams(window.location.search || "");
      const next = (params.get("next") || "").trim();

      // only allow safe same-site paths
      if (next && next.startsWith("/") && !next.startsWith("//")) {
        navigate(next);
      } else {
        navigate("/me");
      }
    } catch (err) {
      setError(err?.message || "Sign in failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="ga-auth">
      <style>{`
        .ga-auth{
          min-height: calc(100vh - 120px);
          padding: 26px 14px 40px;
          display:flex;
          align-items:flex-start;
          justify-content:center;
        }

        .ga-auth__wrap{
          width: min(980px, 100%);
          display:grid;
          grid-template-columns: 1.1fr 0.9fr;
          gap: 18px;
          align-items: stretch;
        }

        @media (max-width: 860px){
          .ga-auth__wrap{ grid-template-columns: 1fr; }
        }

        .ga-auth__promo{
          border-radius: 18px;
          background:
            radial-gradient(1200px 600px at 20% 10%, rgba(255, 0, 64, 0.18), transparent 60%),
            radial-gradient(900px 500px at 90% 30%, rgba(255, 70, 70, 0.10), transparent 55%),
            rgba(8, 10, 16, 0.62);
          border: 1px solid rgba(255,255,255,0.08);
          box-shadow: 0 14px 50px rgba(0,0,0,0.55);
          padding: 18px 18px 20px;
          position: relative;
          overflow:hidden;
        }

        .ga-auth__promo:before{
          content:"";
          position:absolute;
          inset:-2px;
          background: radial-gradient(500px 220px at 20% 20%, rgba(255,0,64,0.18), transparent 55%);
          pointer-events:none;
        }

        .ga-auth__pill{
          display:inline-flex;
          align-items:center;
          gap: 10px;
          padding: 8px 12px;
          border-radius: 999px;
          font-size: 12px;
          font-weight: 700;
          letter-spacing: .04em;
          color: rgba(255,255,255,0.92);
          background: rgba(0,0,0,0.35);
          border: 1px solid rgba(255,255,255,0.10);
          position: relative;
          z-index: 1;
        }

        .ga-auth__dot{
          width: 9px;
          height: 9px;
          border-radius: 999px;
          background: #ff0033;
          box-shadow: 0 0 12px rgba(255,0,51,0.9);
        }

        .ga-auth__promo h1{
          position: relative;
          z-index:1;
          margin: 12px 0 10px;
          font-size: clamp(28px, 4vw, 44px);
          line-height: 1.05;
          color: #ffffff;
          text-shadow: 0 10px 30px rgba(0,0,0,0.6);
        }

        .ga-auth__promo p{
          position: relative;
          z-index:1;
          margin: 0;
          color: rgba(235,240,255,0.78);
          font-size: 14px;
          line-height: 1.55;
          max-width: 58ch;
        }

        .ga-auth__promo ul{
          position: relative;
          z-index:1;
          margin: 14px 0 0;
          padding-left: 18px;
          color: rgba(235,240,255,0.78);
          font-size: 13px;
          line-height: 1.5;
        }

        .ga-auth__card{
          border-radius: 18px;
          background:
            radial-gradient(900px 460px at 15% 10%, rgba(255,0,64,0.15), transparent 60%),
            rgba(8, 10, 16, 0.72);
          border: 1px solid rgba(255,255,255,0.10);
          box-shadow: 0 14px 50px rgba(0,0,0,0.60);
          padding: 18px;
          backdrop-filter: blur(10px);
          -webkit-backdrop-filter: blur(10px);
          position: relative;
          overflow:hidden;
        }

        .ga-auth__card:after{
          content:"";
          position:absolute;
          inset:0;
          border-radius: 18px;
          pointer-events:none;
          box-shadow: inset 0 0 0 1px rgba(255,0,64,0.14);
        }

        .ga-auth__title{
          margin: 6px 0 6px;
          font-size: 22px;
          font-weight: 800;
          color: #fff;
          letter-spacing: .01em;
        }

        .ga-auth__sub{
          margin: 0 0 14px;
          font-size: 13px;
          color: rgba(235,240,255,0.72);
        }

        .ga-auth__error{
          margin: 10px 0 12px;
          border-radius: 12px;
          padding: 10px 12px;
          background: rgba(255,0,64,0.10);
          border: 1px solid rgba(255,0,64,0.22);
          color: rgba(255,255,255,0.92);
          font-size: 13px;
        }

        .ga-auth__form{
          display:flex;
          flex-direction:column;
          gap: 10px;
        }

        .ga-auth__label{
          font-size: 12px;
          font-weight: 700;
          letter-spacing: .04em;
          color: rgba(235,240,255,0.70);
          text-transform: uppercase;
          margin-top: 6px;
        }

        .ga-auth__input{
          width: 100%;
          border-radius: 12px;
          padding: 12px 12px;
          font-size: 14px;
          color: rgba(255,255,255,0.95);
          background: rgba(0,0,0,0.35);
          border: 1px solid rgba(255,255,255,0.10);
          outline: none;
        }

        .ga-auth__input:focus{
          border-color: rgba(255,0,64,0.40);
          box-shadow: 0 0 0 3px rgba(255,0,64,0.18);
        }

        .ga-auth__btn{
          margin-top: 10px;
          width: 100%;
          border-radius: 14px;
          padding: 12px 14px;
          font-size: 14px;
          font-weight: 900;
          letter-spacing: .04em;
          cursor: pointer;
          border: 1px solid rgba(255,0,64,0.35);
          color: #fff;
          background: linear-gradient(135deg, rgba(255,0,64,0.95), rgba(255,65,65,0.85));
          box-shadow: 0 10px 24px rgba(255,0,64,0.20);
          transition: transform .12s ease, filter .12s ease, box-shadow .12s ease;
        }

        .ga-auth__btn:hover{ filter: brightness(1.03); transform: translateY(-1px); }
        .ga-auth__btn:disabled{ opacity: 0.65; cursor: not-allowed; transform: none; }

        .ga-auth__links{
          margin-top: 12px;
          display:flex;
          flex-direction:column;
          gap: 8px;
          font-size: 13px;
          color: rgba(235,240,255,0.78);
        }

        .ga-auth__links a{
          color: rgba(255,255,255,0.92);
          text-decoration: none;
          border-bottom: 1px solid rgba(255,255,255,0.14);
          width: fit-content;
        }

        .ga-auth__links a:hover{
          border-bottom-color: rgba(255,0,64,0.55);
        }
      `}</style>

      <div className="ga-auth__wrap">
        <div className="ga-auth__promo">
          <div className="ga-auth__pill">
            <span className="ga-auth__dot" />
            GAStreaming.live • Member Access
          </div>

          <h1>Sign in to GAStreaming</h1>
          <p>
            Tap in. Watch live. Comment. Build your profile. This is the official hub — and we’re
            just getting started.
          </p>

          <ul>
            <li>Access your profile & uploads</li>
            <li>Community feed + comment threads</li>
            <li>Future: Subscribe system + perks</li>
          </ul>
        </div>

        <div className="ga-auth__card">
          <div className="ga-auth__title">Sign in</div>
          <div className="ga-auth__sub">Enter your email and password to continue.</div>

          {error ? <div className="ga-auth__error">{error}</div> : null}

          <form className="ga-auth__form" onSubmit={handleSubmit}>
            <div className="ga-auth__label">Email</div>
            <input
              className="ga-auth__input"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              autoComplete="email"
              placeholder="you@example.com"
            />

            <div className="ga-auth__label">Password</div>
            <input
              className="ga-auth__input"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="current-password"
              placeholder="••••••••"
            />

            <button className="ga-auth__btn" disabled={submitting} type="submit">
              {submitting ? "Signing in..." : "Sign in"}
            </button>
          </form>

          <div className="ga-auth__links">
            <Link to="/forgot-password">Forgot password?</Link>
            <div>
              No account? <Link to="/register">Create one</Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
